import React from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const UsageAnalytics = () => {
  const trendData = [
    { date: '01/01', prompts: 12 },
    { date: '01/02', prompts: 19 },
    { date: '01/03', prompts: 15 },
    { date: '01/04', prompts: 25 },
    { date: '01/05', prompts: 22 },
    { date: '01/06', prompts: 30 },
    { date: '01/07', prompts: 28 }
  ];

  const toolUsageData = [
    { tool: 'ChatGPT', count: 45 },
    { tool: 'Claude', count: 32 },
    { tool: 'Midjourney', count: 28 },
    { tool: 'DALL-E', count: 18 },
    { tool: 'Gemini', count: 15 }
  ];

  const qualityDistribution = [
    { name: 'Excellent', value: 35, color: 'var(--color-success)' },
    { name: 'Good', value: 45, color: 'var(--color-warning)' },
    { name: 'Fair', value: 20, color: 'var(--color-error)' }
  ];

  const stats = [
    {
      label: 'Total Prompts',
      value: '138',
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'This Week',
      value: '28',
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: 'Favorites',
      value: '42',
      icon: 'Star',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      label: 'Avg Quality',
      value: '82',
      icon: 'Award',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    }
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {stats?.map((stat) => (
          <div key={stat?.label} className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
                <Icon name={stat?.icon} size={20} className={stat?.color} />
              </div>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
            <div className="text-xs md:text-sm text-muted-foreground">{stat?.label}</div>
          </div>
        ))}
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Prompt Creation Trend */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Prompt Creation Trend</h3>
          <div className="w-full h-48 md:h-64" aria-label="Prompt creation trend line chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Line type="monotone" dataKey="prompts" stroke="var(--color-primary)" strokeWidth={2} dot={{ fill: 'var(--color-primary)' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Most Used Tools */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Most Used AI Tools</h3>
          <div className="w-full h-48 md:h-64" aria-label="Most used AI tools bar chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="tool" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="count" fill="var(--color-secondary)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quality Distribution */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Quality Score Distribution</h3>
          <div className="w-full h-48 md:h-64" aria-label="Quality score distribution pie chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={qualityDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {qualityDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Success Metrics</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Prompt Reuse Rate</span>
                <span className="text-sm font-semibold text-foreground">68%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: '68%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Average Quality Score</span>
                <span className="text-sm font-semibold text-foreground">82/100</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-success rounded-full" style={{ width: '82%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Favorite Rate</span>
                <span className="text-sm font-semibold text-foreground">30%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-warning rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Weekly Growth</span>
                <span className="text-sm font-semibold text-success">+24%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-secondary rounded-full" style={{ width: '24%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;